import React, { useState } from 'react';
import { HelpCircle, X, Send, Loader2, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { useI18n } from '../contexts/I18nContext';
import api from '../utils/api';
import { toast } from 'sonner';

/**
 * AIQuestionHelper - Floating question mark button that opens an AI chat panel
 * Users can ask questions about the current word, sentence, or lesson context
 * 
 * Props:
 * - contextType: 'word' | 'sentence' | 'lesson' | 'text'
 * - contextId: ID of the context item (optional)
 * - contextText: The actual word/sentence being studied
 * - language: The language being studied (e.g., 'Norwegian')
 * - isOpen: (optional) External control for open state
 * - onOpenChange: (optional) Callback when open state changes
 * - hideFloatingButton: (optional) Hide the floating button (when using inline)
 */
export default function AIQuestionHelper({ 
  contextType = 'text', 
  contextId = null, 
  contextText = '', 
  language = '',
  isOpen: externalIsOpen,
  onOpenChange,
  hideFloatingButton = false
}) {
  const { t } = useI18n();
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  
  // Use external control if provided, otherwise use internal state
  const isOpen = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen;
  const setIsOpen = (value) => {
    if (onOpenChange) {
      onOpenChange(value);
    } else {
      setInternalIsOpen(value);
    }
  };
  
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);

  const handleAsk = async () => {
    if (!question.trim()) return;
    
    setLoading(true);
    setAnswer(null);
    
    try {
      const response = await api.post('/ai-helper/ask', {
        question: question.trim(),
        context_type: contextType,
        context_id: contextId,
        context_text: contextText,
        language: language
      });
      
      const data = response.data;
      
      // Add to history
      setHistory(prev => [...prev, {
        question: question.trim(),
        answer: data.answer,
        isRelevant: data.is_relevant
      }]);
      
      setAnswer(data);
      setQuestion('');
      
      if (!data.is_relevant) {
        toast.info(t('aiHelper.limitedToSubject') || 'Please ask about the current subject');
      }
    } catch (error) {
      if (error.response?.status === 429) {
        toast.error(t('aiHelper.dailyLimitReached') || 'Daily question limit reached');
      } else {
        toast.error(t('aiHelper.errorOccurred') || 'Failed to get answer');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAsk();
    }
  };

  const getContextLabel = () => {
    switch (contextType) {
      case 'word':
        return t('aiHelper.studyingWord') || 'Studying word';
      case 'sentence':
        return t('aiHelper.studyingSentence') || 'Studying sentence';
      case 'lesson':
        return t('aiHelper.inLesson') || 'In lesson';
      default:
        return t('aiHelper.readingText') || 'Reading text';
    }
  };

  return (
    <>
      {/* Floating Button - positioned higher on mobile to avoid covering pagination and navbar */}
      {!hideFloatingButton && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-36 sm:bottom-6 right-4 sm:right-6 z-40 w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 hover:from-amber-400 hover:to-orange-500 shadow-lg shadow-amber-500/30 flex items-center justify-center transition-all hover:scale-110 active:scale-95"
          data-testid="ai-helper-button"
          title={t('aiHelper.askQuestion') || 'Ask a question about this lesson'}
        >
          <HelpCircle className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
          
          {/* Pulse animation */}
          <span className="absolute inset-0 rounded-full bg-amber-400 animate-ping opacity-30" />
        </button>
      )}

      {/* Modal Panel */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 sm:p-6">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setIsOpen(false)}
          />
          
          {/* Panel */}
          <div className="relative w-full max-w-md bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4 duration-300">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700 bg-slate-800/50">
              <div className="flex items-center gap-2">
                <div className="p-1.5 rounded-lg bg-amber-500/20">
                  <MessageCircle className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">
                    {t('aiHelper.title') || 'Learning Assistant'}
                  </h3>
                  <p className="text-xs text-slate-400">
                    {t('aiHelper.subtitle') || 'Ask about this lesson'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1.5 rounded-lg hover:bg-slate-700 transition-colors"
              >
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>

            {/* Context Info */}
            {contextText && (
              <div className="px-4 py-2 bg-slate-800/30 border-b border-slate-700/50">
                <p className="text-xs text-slate-500 mb-1">{getContextLabel()}</p>
                <p className="text-sm text-slate-300 font-medium truncate">
                  {contextText.length > 80 ? contextText.slice(0, 80) + '...' : contextText}
                </p>
                {language && (
                  <p className="text-xs text-amber-400 mt-1">{language}</p>
                )}
              </div>
            )}

            {/* Chat History */}
            <div className="max-h-64 overflow-y-auto p-4 space-y-4">
              {history.length === 0 && !answer && (
                <div className="text-center py-6">
                  <HelpCircle className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-400 text-sm">
                    {t('aiHelper.askAnything') || 'Ask anything about this lesson'}
                  </p>
                  <p className="text-slate-500 text-xs mt-1">
                    {t('aiHelper.examples') || 'e.g., "What does this mean?", "How do I use this?"'}
                  </p>
                </div>
              )}
              
              {history.map((item, idx) => (
                <div key={idx} className="space-y-2">
                  {/* User Question */}
                  <div className="flex justify-end">
                    <div className="bg-amber-500/20 text-amber-100 px-3 py-2 rounded-xl rounded-br-sm max-w-[85%]">
                      <p className="text-sm">{item.question}</p>
                    </div>
                  </div>
                  
                  {/* AI Answer */}
                  <div className="flex justify-start">
                    <div className={`px-3 py-2 rounded-xl rounded-bl-sm max-w-[85%] ${
                      item.isRelevant 
                        ? 'bg-slate-700/50 text-slate-200' 
                        : 'bg-slate-800 text-slate-400 border border-slate-700'
                    }`}>
                      <p className="text-sm whitespace-pre-wrap">{item.answer}</p>
                    </div>
                  </div>
                </div>
              ))}
              
              {/* Loading */}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-slate-700/50 px-4 py-3 rounded-xl rounded-bl-sm">
                    <div className="flex items-center gap-2 text-slate-400">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="text-sm">{t('aiHelper.thinking') || 'Thinking...'}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Input Area */}
            <div className="p-4 border-t border-slate-700 bg-slate-800/30">
              <div className="flex gap-2">
                <Textarea
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={t('aiHelper.placeholder') || 'Ask about this word or sentence...'}
                  className="flex-1 min-h-[44px] max-h-[120px] resize-none bg-slate-800 border-slate-600 text-white placeholder:text-slate-500 focus:border-amber-500"
                  disabled={loading}
                  data-testid="ai-helper-input"
                />
                <Button
                  onClick={handleAsk}
                  disabled={loading || !question.trim()}
                  className="h-auto px-3 bg-amber-500 hover:bg-amber-600 text-white"
                  data-testid="ai-helper-send"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-2 text-center">
                {t('aiHelper.limitedToContext') || 'Questions limited to current learning context'}
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
